import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfigProvider, useConfig } from "@/context/ConfigContext";
import { ConfirmDialogProvider } from "@/context/ConfirmDialogContext";
import { ThemeProvider } from "@/context/ThemeContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import AudiencesPage from "@/pages/AudiencesPage";
import AudienceDetailPage from "@/pages/AudienceDetailPage";
import CampaignsPage from "@/pages/CampaignsPage";
import CampaignBuilderPage from "@/pages/CampaignBuilderPage";
import CampaignAnalyticsPage from "@/pages/CampaignAnalyticsPage";
import CampaignEngagementPage from "@/pages/CampaignEngagementPage";
import TemplatesPage from "@/pages/TemplatesPage";
import TemplateEditorPage from "@/pages/TemplateEditorPage";
import AdminPage from "@/pages/AdminPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import SendingDomainsPage from "@/pages/SendingDomainsPage";
import WorkflowsPage from "@/pages/WorkflowsPage";
import SuppressionsPage from "@/pages/SuppressionsPage";
import SegmentsPage from "@/pages/SegmentsPage";
import BillingPage from "@/pages/BillingPage";
import SettingsPage from "@/pages/SettingsPage";
import AppLayout from "@/components/AppLayout";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-display text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return children;
}

function FeatureRoute({ enabled, children }) {
  if (!enabled) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { config, loading: configLoading } = useConfig();
  const { features } = config;

  if (configLoading) {
    return <div className="min-h-screen flex items-center justify-center font-display text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/register"
        element={
          features.public_registration ? <RegisterPage /> : <Navigate to="/login" replace />
        }
      />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/audiences" element={<AudiencesPage />} />
        <Route path="/audiences/:id" element={<AudienceDetailPage />} />
        <Route path="/segments" element={<SegmentsPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/new" element={<CampaignBuilderPage />} />
        <Route path="/campaigns/:id/edit" element={<CampaignBuilderPage />} />
        <Route path="/campaigns/:id/analytics" element={<CampaignAnalyticsPage />} />
        <Route path="/campaigns/:id" element={<CampaignAnalyticsPage />} />
        <Route path="/campaigns/:id/engagement/:kind" element={<CampaignEngagementPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/templates/new" element={<TemplateEditorPage />} />
        <Route path="/templates/:id/edit" element={<TemplateEditorPage />} />
        <Route path="/automations" element={<WorkflowsPage />} />
        <Route path="/domains" element={<SendingDomainsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/suppressions" element={<SuppressionsPage />} />
        <Route
          path="/billing"
          element={
            <FeatureRoute enabled={features.stripe_billing}>
              <BillingPage />
            </FeatureRoute>
          }
        />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ConfigProvider>
      <ThemeProvider>
        <AuthProvider>
          <ConfirmDialogProvider>
            <Toaster position="top-right" richColors />
            <div className="App">
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </div>
          </ConfirmDialogProvider>
        </AuthProvider>
      </ThemeProvider>
    </ConfigProvider>
  );
}

export default App;
